/**
 * Backend WebSocket client — single-session enforcement + live push events +
 * LIVENESS OMURGASI (Faz 1). Heartbeat (ping/pong) ile connection store'u sürer:
 * pong gelmezse saniyeler içinde 'offline' → tüm canlı UI tek sinyalden donar.
 */

import { connOnOpen, connOnHeartbeat, connOnDown } from '@/store/connection';
import type { AgentDurations } from './api';

const PING_MS = 15_000;        // heartbeat aralığı
const STALE_MS = 38_000;       // son pong'dan bu kadar geçtiyse backend ölü/hung → kopar
const WATCHDOG_MS = 5_000;     // heartbeat tazelik kontrolü
const MAX_BACKOFF_MS = 30_000; // reconnect üst sınırı

type Listener<T = any> = (msg: T) => void;

export interface SessionWsEvents {
  open: () => void;
  close: (code: number, reason: string) => void;
  session_kept: (msg: { session_id: string }) => void;
  force_logout: (msg: { reason: string; logged_in_from?: string }) => void;
  config_update: (msg: { data: any }) => void;
  directory_update: (msg: { data: any[] }) => void;
  crm_template_update: (msg: { data: any[] }) => void;
  notification_new: (msg: { notification: any }) => void;
  /** Heartbeat'e bindirilen sunucu-otoriteli süre snapshot'ı (Faz 2). */
  durations: (durations: AgentDurations) => void;
  error: (err: Error) => void;
}

export class SessionWs {
  private ws: WebSocket | null = null;
  private pingTimer?: number;
  private reconnectTimer?: number;
  private heartbeatTimer?: number;
  private intentionallyClosed = false;
  private reconnectAttempt = 0;
  private openedAt = 0;
  private lastPongAt = 0;
  private listeners = new Map<string, Set<Function>>();
  /** Aktif topic abonelikleri — reconnect sonrası otomatik yeniden abone olunur. */
  private subscribedTopics = new Set<string>();

  constructor(private readonly url: string, private readonly token: string) {}

  on<K extends keyof SessionWsEvents>(ev: K, fn: SessionWsEvents[K]): () => void {
    if (!this.listeners.has(ev)) this.listeners.set(ev, new Set());
    this.listeners.get(ev)!.add(fn);
    return () => this.listeners.get(ev)?.delete(fn);
  }
  private emit(ev: string, ...args: any[]) {
    this.listeners.get(ev)?.forEach((fn) => {
      try { (fn as any)(...args); } catch (e) { console.error(e); }
    });
  }

  /**
   * Topic'e abone ol (eski projenin watch modeli). handler topic verisini alır.
   * Reconnect olursa otomatik yeniden abone olunur. Dönen fn aboneliği bırakır.
   */
  subscribe(topic: string, handler: (data: any) => void): () => void {
    const wrapped = (msg: any) => handler(msg?.data);
    if (!this.listeners.has(topic)) this.listeners.set(topic, new Set());
    this.listeners.get(topic)!.add(wrapped);
    this.subscribedTopics.add(topic);
    // Bağlantı açıksa hemen sunucuya bildir
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send({ event: 'subscribe', data: { topic } });
    }
    return () => {
      const set = this.listeners.get(topic);
      set?.delete(wrapped);
      if (!set || set.size === 0) {
        this.subscribedTopics.delete(topic);
        this.send({ event: 'unsubscribe', data: { topic } });
      }
    };
  }

  connect() {
    this.intentionallyClosed = false;
    const url = `${this.url}?token=${encodeURIComponent(this.token)}`;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.openedAt = Date.now();
      connOnOpen(); // socket açık ama henüz pong yok → 'connecting'
      this.emit('open');
      // Reconnect sonrası topic aboneliklerini tazele (server yeni bağlantıyı bilmez)
      for (const topic of this.subscribedTopics) {
        this.send({ event: 'subscribe', data: { topic } });
      }
      // Heartbeat: hemen bir ping + periyodik ping; ayrı watchdog tazeliği kontrol eder.
      this.sendPing();
      this.pingTimer = window.setInterval(() => this.sendPing(), PING_MS);
      this.heartbeatTimer = window.setInterval(() => this.checkHeartbeat(), WATCHDOG_MS);
    };

    this.ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        const type = msg.type || msg.event;
        // pong = heartbeat → backend canlı + sunucu saati senkron. reconnect
        // backoff'unu sıfırla (canlılık kanıtı). Generic emit'e düşmesine gerek yok.
        if (type === 'pong') {
          this.lastPongAt = Date.now();
          this.reconnectAttempt = 0;
          connOnHeartbeat(msg.ts ? new Date(msg.ts).getTime() : Date.now());
          // Sunucu-otoriteli süre snapshot'ı varsa yay → AppShell query cache'e yazar.
          if (msg.durations) this.emit('durations', msg.durations);
          return;
        }
        if (type && this.listeners.has(type)) {
          this.emit(type as any, msg);
        }
      } catch { /* */ }
    };

    this.ws.onclose = (e) => {
      this.cleanupTimers();
      connOnDown(); // bağlantı koptu → offline (canlı UI donar)
      this.emit('close', e.code, e.reason);
      // force_logout (4002) / unauthorized (4001) / kasıtlı kapanış → reconnect yok
      if (e.code === 4002 || e.code === 4001 || this.intentionallyClosed) return;
      // Exponential backoff + jitter — kesinti sonrası tüm istemcilerin AYNI anda
      // geri bağlanıp sunucuyu dövmesini (thundering herd) önler.
      const base = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** this.reconnectAttempt);
      const delay = base + Math.random() * base * 0.3;
      this.reconnectAttempt += 1;
      this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
    };

    this.ws.onerror = () => this.emit('error', new Error('ws_error'));
  }

  close() {
    this.intentionallyClosed = true;
    this.cleanupTimers();
    try { this.ws?.close(); } catch { /* */ }
    this.ws = null;
  }

  send(msg: Record<string, unknown>) {
    // Sadece OPEN iken gönder — CONNECTING/CLOSED socket'te send() sessizce
    // kaybolur (örn. reconnect sırasında gelen call_event publish'i). Mesaj
    // kaybı yerine drop + ilgili akış 'open'da yeniden yollar (sendPing ile aynı
    // disiplin). Topic abonelikleri zaten 'open'da otomatik tazeleniyor.
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    try { this.ws.send(JSON.stringify(msg)); } catch { /* */ }
  }

  private sendPing() {
    // Sadece OPEN iken gönder — kapanan/kapanmış socket'te send() "already in
    // CLOSING or CLOSED state" konsol uyarısı basıyordu (zararsız ama gürültü).
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    try { this.ws.send(JSON.stringify({ event: 'ping' })); } catch { /* */ }
  }

  /** Pong gecikti mi? Backend ölü/hung olabilir (socket açık ama cevap yok) —
   *  socket'i kapat → onclose reconnect'i tetikler, UI offline'a düşer. */
  private checkHeartbeat() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const ref = this.lastPongAt || this.openedAt;
    if (Date.now() - ref > STALE_MS) {
      connOnDown();
      try { this.ws.close(); } catch { /* onclose reconnect'i sürer */ }
    }
  }

  private cleanupTimers() {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
  }
}
